/* eslint-disable @typescript-eslint/no-explicit-any */
import { Model } from '@adobe/aem-spa-page-model-manager';
import { ModelProps } from '@adobe/aem-react-editable-components';
import { KEY_BOARD_EVENTS, TrackingPropsInterface } from './enums/enums';

export const canUseDOM = !!(typeof window !== 'undefined' && window.document);

//Utility for converting nested container json to tree format

export type AEMModelType = Model & ModelProps;

export type StringIndexedObject = {
  [key: string]: any;
};

export interface ScrollSettings {
  isNavComponent?: boolean;
  isStickyOnScrollDown?: boolean;
  isStickyOnScrollUp?: boolean;
}

export interface ScrollStoreModel extends Model {
  componentId?: string;
  scrollSetting?: ScrollSettings;
  ':children'?: {
    [key: string]: ScrollStoreModel;
  };
  ':items'?: {
    [key: string]: ScrollStoreModel;
  };
}

export interface AEMModel extends AEMModelType {
  resourceProperties?: any;
  itemKey?: string;
  backgroundMedia?: any;
  trackingProperties?: TrackingPropsInterface;
  componentId?: string;
  scrollSetting?: ScrollSettings;
  ':children'?: {
    [key: string]: ScrollStoreModel;
  };
  ':items'?: {
    [key: string]: ScrollStoreModel;
  };
  styleclass?: string;
}
// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface SortedModelArray extends Array<AEMModel> {}
export interface AEMContainerProps {
  cqPath?: string;
  isAuthorMode?: boolean;
}

export const sortAEMModelItemsByOrder = (data: AEMModel): SortedModelArray => {
  if (!data) return [];
  const itemsOrder = data[':itemsOrder'] || data?.cqItemsOrder || [];
  const items = data[':items'] || data?.cqItems || {};
  return itemsOrder?.length
    ? itemsOrder.map((itemKey: string) => {
        return { ...items[itemKey], itemKey };
      })
    : [];
};

//TODO:Importing from headless utils is giving error.So kept here for now. Need to remove after the issue is fixed.
export function transformToCQ(propKey: string) {
  const tempKey = propKey.substring(1);

  return 'cq' + tempKey.substring(0, 1).toUpperCase() + tempKey.substring(1);
}

export const respGridUtil = (item: any) => {
  if (!item || !Object.keys(item).length) {
    return { cqPath: '' };
  }

  const keys = Object.getOwnPropertyNames(item);
  const props: any = {};

  keys.forEach((key: string) => {
    const propKey = key.startsWith(':') ? transformToCQ(key) : key;
    props[propKey] = item[key] || '';
  });

  return props;
};

/**
 *
 * @param text
 * Remove the parent tag from RTE text
 * <p>Lorem Ipsum has been the <sub>industry's standard</sub> dummy text ever</p>
 * =>Lorem Ipsum has been the <sub>industry's standard</sub> dummy text ever
 */
export const removeParentTagRTE = (textRTE: string): string => {
  return textRTE.substring(textRTE.indexOf('>') + 1, textRTE.lastIndexOf('<'));
};

export const getEventKeyTypes = (e: React.KeyboardEvent<HTMLElement>) => {
  const { ENTER, TAB, ESCAPE, ARROW_UP, ARROW_DOWN, ARROW_RIGHT, ARROW_LEFT, SPACEBAR } = KEY_BOARD_EVENTS ?? {};
  return {
    isEnterKey: e.key === ENTER?.key || e.keyCode === ENTER?.keyCode,
    isTabKey: e.key === TAB?.key || e.keyCode === TAB?.keyCode,
    isEscapseKey: e.key === ESCAPE?.key || e.keyCode === ESCAPE?.keyCode,
    isUpArrowKey: e.key === ARROW_UP?.key || e.keyCode === ARROW_UP.keyCode,
    isDownArrowKey: e.key === ARROW_DOWN?.key || e.keyCode === ARROW_DOWN?.keyCode,
    isRightArrowKey: e.key === ARROW_RIGHT?.key || e.keyCode === ARROW_RIGHT?.keyCode,
    isLeftArrowKey: e.key === ARROW_LEFT?.key || e.keyCode === ARROW_LEFT?.keyCode,
    isSpaceBarKey: e.key === SPACEBAR?.key || e.keyCode === SPACEBAR?.keyCode,
  };
};

/**
 *
 * Throttling any event handler to avoid excessive function calls
 * @param func @param delay
 * @returns the handler function "func" which executes after every 500 ms
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const throttle = (func: any, delay: number) => {
  let timeoutId: NodeJS.Timeout;
  let lastExecTime = 0;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (...args: any[]) => {
    const currentTime = new Date().getTime();
    const elapsedTime = currentTime - lastExecTime;

    if (!timeoutId || elapsedTime >= delay) {
      func.apply(this, args);
      lastExecTime = currentTime;
    }

    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      lastExecTime = new Date().getTime();
    }, delay);
  };
};
export const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * This function updates the keys of an object based on a provided key mapping.
 *
 * @param {StringIndexedObject} aemModelObj - The original object whose keys are to be updated.
 *                                    It must be a non-null object.
 *
 * @throws {Error} Will throw an error if the provided obj or keyMap is not an object or is null.
 *
 * @returns {StringIndexedObject} A new object with updated keys.
 *                                If a key in the original object is found in the keyMap,
 *                                it will be replaced with the corresponding value from the keyMap.
 *                                If a key in the original object is not found in the keyMap,
 *                                it will remain the same.
 *                                Note: The function does not mutate the original object.
 */
export const updateAEMCQKeys = (aemModelObj: StringIndexedObject): StringIndexedObject => {
  const cqKeyMap: { [key: string]: string } = {
    ':items': 'cqItems',
    ':itemsOrder': 'cqItemsOrder',
    ':type': 'cqType',
  };

  if (typeof aemModelObj !== 'object' || aemModelObj === null) {
    throw new Error('Invalid object provided');
  }

  const updatedObject: StringIndexedObject = { ...aemModelObj };

  for (const key in cqKeyMap) {
    if (Object.prototype.hasOwnProperty.call(aemModelObj, key)) {
      const updatedKey = cqKeyMap[key];
      // Only update if updatedKey is defined and does not exist in the original object
      if (updatedKey && !Object.prototype.hasOwnProperty.call(aemModelObj, updatedKey)) {
        updatedObject[updatedKey] = aemModelObj[key];
        delete updatedObject[key];
      }
    }
  }

  return updatedObject;
};

export const getLoyaltyTheme = (inputString: string | undefined) => {
  const regex = /\b(memberSolid|memberSolidAlt|silverSolid|goldSolid|platinumSolid|titaniumSolid|ambassadorSolid)\b/g;
  const matches = inputString?.match(regex);

  if (matches) {
    const key = matches[0];
    return key;
  } else {
    return;
  }
};

// This helper maps the current portfolio brand logo tag values (CSS class names) to the future SVG-based logo implementation, where CSS classes will no longer be used. This mapping will be removed once the tag values are updated to SVG file names. This will enable the use of the new logo container without having to re-author the components that are using logo tags
export const getLogoName = (brandTag: string) => {
  let brandSVGName;

  switch (brandTag) {
    case 'portfolio-icon-ritz-carlton':
      brandSVGName = 'rz';
      break;
    case 'portfolio-icon-edition':
      brandSVGName = 'eb';
      break;
    case 'portfolio-icon-jw':
      brandSVGName = 'jw';
      break;
    case 'portfolio-icon-autograph-collection':
      brandSVGName = 'ak';
      break;
    case 'portfolio-icon-renaissance':
      brandSVGName = 'br';
      break;
    case 'portfolio-icon-delta-hotels':
      brandSVGName = 'de-portfolio';
      break;
    case 'portfolio-icon-marriott':
      brandSVGName = 'mc';
      break;
    case 'portfolio-icon-marriott-executive-apartments':
      brandSVGName = 'er';
      break;
    case 'portfolio-icon-vacation-club':
      brandSVGName = 'mv';
      break;
    case 'portfolio-icon-gaylord':
      brandSVGName = 'ge';
      break;
    case 'portfolio-icon-ac-hotels':
      brandSVGName = 'ar';
      break;
    case 'portfolio-icon-courtyard':
      brandSVGName = 'cy-portfolio';
      break;
    case 'portfolio-icon-residence-inn':
      brandSVGName = 'ri-portfolio';
      break;
    case 'portfolio-icon-springhill-suites':
      brandSVGName = 'sh';
      break;
    case 'portfolio-icon-fairfield-inn':
      brandSVGName = 'fi-portfolio';
      break;
    case 'portfolio-icon-towneplace-suites':
      brandSVGName = 'ts-portfolio';
      break;
    case 'portfolio-icon-protea-hotels':
      brandSVGName = 'pr-portfolio';
      break;
    case 'portfolio-icon-moxy':
      brandSVGName = 'ox';
      break;
    case 'portfolio-icon-st-regis':
      brandSVGName = 'xr';
      break;
    case 'portfolio-icon-luxury-collection':
      brandSVGName = 'lc';
      break;
    case 'portfolio-icon-w-hotels':
      brandSVGName = 'wh';
      break;
    case 'portfolio-icon-westin':
      brandSVGName = 'wi';
      break;
    case 'portfolio-icon-sheraton':
      brandSVGName = 'si';
      break;
    case 'portfolio-icon-le-meridien':
      brandSVGName = 'md';
      break;
    case 'portfolio-icon-tribute-portfolio':
      brandSVGName = 'tx-portfolio';
      break;
    case 'portfolio-icon-design-hotels':
      brandSVGName = 'ds';
      break;
    case 'portfolio-icon-aloft':
      brandSVGName = 'al';
      break;
    case 'portfolio-icon-four-points':
      brandSVGName = 'fp';
      break;
    case 'portfolio-icon-element':
      brandSVGName = 'el';
      break;
    case 'portfolio-icon-rcr':
      brandSVGName = 'rzr';
      break;
    case 'portfolio-icon-hotels-and-villas':
      brandSVGName = 'NA';
      break;
    case 'portfolio-icon-hvmi':
      brandSVGName = 'hv';
      break;
    case 'portfolio-icon-mvc':
      brandSVGName = 'mv';
      break;
    case 'portfolio-icon-xe':
      brandSVGName = 'xe';
      break;
    case 'portfolio-icon-ba':
      brandSVGName = 'ba';
      break;
    case 'portfolio-icon-mg':
      brandSVGName = 'mg';
      break;
    case 'portfolio-icon-tours-activities':
      brandSVGName = 'NA';
      break;
    case 'portfolio-icon-events':
      brandSVGName = 'NA';
      break;
    case 'portfolio-icon-boutiques':
      brandSVGName = 'NA';
      break;
    case 'portfolio-icon-moments':
      brandSVGName = 'NA';
      break;
    case 'portfolio-icon-sheraton-vacation-club':
      brandSVGName = 'sv';
      break;
    case 'portfolio-icon-westin-vacation-club':
      brandSVGName = 'wv';
      break;
    case 'portfolio-icon-xf':
      brandSVGName = 'xf-portfolio';
      break;
    case 'portfolio-icon-rs':
      brandSVGName = 'rs';
      break;
    case 'portfolio-icon-bg':
      brandSVGName = 'bg';
      break;
    case 'portfolio-icon-sn':
      brandSVGName = 'sn';
      break;
    case 'brand-logo-MR':
      brandSVGName = 'mr';
      break;
    case 'brand-logo-RZ':
      brandSVGName = 'rz';
      break;
    case 'brand-logo-EB':
      brandSVGName = 'eb';
      break;
    case 'brand-logo-JW':
      brandSVGName = 'jw';
      break;
    case 'brand-logo-AK':
      brandSVGName = 'ak';
      break;
    case 'brand-logo-BR':
      brandSVGName = 'br';
      break;
    case 'brand-logo-DE':
      brandSVGName = 'de';
      break;
    case 'brand-logo-MC':
      brandSVGName = 'mc';
      break;
    case 'brand-logo-ER':
      brandSVGName = 'er';
      break;
    case 'brand-logo-MV':
      brandSVGName = 'mv';
      break;
    case 'brand-logo-GE':
      brandSVGName = 'ge';
      break;
    case 'brand-logo-AR':
      brandSVGName = 'ar';
      break;
    case 'brand-logo-CY':
      brandSVGName = 'cy';
      break;
    case 'brand-logo-RI':
      brandSVGName = 'ri';
      break;
    case 'brand-logo-SH':
      brandSVGName = 'sh';
      break;
    case 'brand-logo-FI':
      brandSVGName = 'fi';
      break;
    case 'brand-logo-TS':
      brandSVGName = 'ts';
      break;
    case 'brand-logo-PR':
      brandSVGName = 'pr';
      break;
    case 'brand-logo-OX':
      brandSVGName = 'ox';
      break;
    case 'brand-logo-XR':
      brandSVGName = 'xr';
      break;
    case 'brand-logo-LC':
      brandSVGName = 'lc';
      break;
    case 'brand-logo-WH':
      brandSVGName = 'wh';
      break;
    case 'brand-logo-WI':
      brandSVGName = 'wi';
      break;
    case 'brand-logo-SI':
      brandSVGName = 'si';
      break;
    case 'brand-logo-MD':
      brandSVGName = 'md';
      break;
    case 'brand-logo-TX':
      brandSVGName = 'tx-portfolio';
      break;
    case 'brand-logo-DS':
      brandSVGName = 'ds';
      break;
    case 'brand-logo-AL':
      brandSVGName = 'al';
      break;
    case 'brand-logo-FP':
      brandSVGName = 'fp';
      break;
    case 'brand-logo-EL':
      brandSVGName = 'el';
      break;
    case 'brand-logo-BG':
      brandSVGName = 'bg';
      break;
    case 'brand-logo-HV':
      brandSVGName = 'hv';
      break;
    case 'brand-logo-MI':
      brandSVGName = 'mi';
      break;
    case 'brand-logo-XE':
      brandSVGName = 'xe';
      break;
    case 'brand-logo-BA':
      brandSVGName = 'ba';
      break;
    case 'brand-logo-XF':
      brandSVGName = 'xf';
      break;
    case 'brand-logo-SV':
      brandSVGName = 'sv';
      break;
    case 'brand-logo-WV':
      brandSVGName = 'wv';
      break;
    case 'brand-logo-MG':
      brandSVGName = 'mg';
      break;
    case 'brand-logo-RS':
      brandSVGName = 'rs';
      break;
    case 'brand-logo-SN':
      brandSVGName = 'sn';
      break;
    case 't-program-logo-m':
      brandSVGName = 'mc';
      break;
    case 't-program-logo-color-s':
      brandSVGName = 'mbs';
      break;
    default:
      brandSVGName = brandTag.toLowerCase();
      break;
  }

  return brandSVGName;
};
